// The PDF of a client invoice, drawn in the browser with jsPDF (already used for order invoices) so the server needs no PDF
// library. Same visual language as the admin and the emails: zinc neutrals, hairline rules, rounded cards.
import { FG, MUTED_FG, BORDER, MUTED, PRIMARY, RADIUS, loadPdfLibs, loadGeist, loadLogo, typography } from './pdfKit.js';
import { calc, balanceOf, displayStatus, fmtMoney } from './invoiceMath.js';

const PILLS = {
  draft:   { bg: [244, 244, 245], fg: [63, 63, 70],   label: 'Draft' },
  issued:  { bg: [254, 243, 199], fg: [146, 64, 14],  label: 'Awaiting payment' },
  partial: { bg: [219, 234, 254], fg: [30, 64, 175],  label: 'Part paid' },
  overdue: { bg: [254, 226, 226], fg: [153, 27, 27],  label: 'Overdue' },
  paid:    { bg: [220, 252, 231], fg: [22, 101, 52],  label: 'Paid' },
  void:    { bg: [244, 244, 245], fg: [113, 113, 122], label: 'Void' },
};
const longDate = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : '—');
const lines = (s) => String(s || '').split('\n').map(t => t.trim()).filter(Boolean);

/**
 * @param {object} p
 * @param {object} p.invoice   as returned by /api/invoices (or the editor's working copy)
 * @param {object} p.business  Settings → Invoicing
 * @param {object} p.site      the store settings (name, logo, emailLogo)
 * @returns {Promise<{ doc: any, filename: string, base64: () => string, blobUrl: () => string }>}
 */
export async function buildInvoicePdf({ invoice, business = {}, site = {} }) {
  await loadPdfLibs();
  const { jsPDF } = window.jspdf;
  let logoOnTile = false;
  let [logo, fonts] = await Promise.all([loadLogo(site?.logo), loadGeist().catch(() => null)]);
  if (!logo && site?.emailLogo) { logo = await loadLogo(site.emailLogo); logoOnTile = true; }
  const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true })   // compressed: the embedded typeface would otherwise make it 1.5 MB;
  const { FAMILY, WEIGHT, type } = typography(doc, fonts);

  const currency = invoice.currency || 'R';
  const money = (n) => fmtMoney(n, currency);
  const t = calc(invoice);
  const { paid, balance } = balanceOf(t.total, invoice.payments);
  const status = displayStatus({ ...invoice, total: t.total }, undefined);
  const pill = PILLS[status] || PILLS.draft;
  const taxInvoice = Boolean(business.vatNumber) && Number(invoice.taxRate) > 0;
  const businessName = business.businessName || site?.name || 'Others.';

  const W = 210, H = 297, M = 16, R = W - M, CW = R - M;
  const card = (x, y, w, h) => { doc.setDrawColor(...BORDER); doc.setLineWidth(0.25); doc.setFillColor(255, 255, 255); doc.roundedRect(x, y, w, h, RADIUS, RADIUS, 'FD'); };
  const rule = (x1, y, x2) => { doc.setDrawColor(...BORDER); doc.setLineWidth(0.25); doc.line(x1, y, x2, y); };
  const wrap = (text, width) => doc.splitTextToSize(String(text), width);

  // ── Letterhead ──
  let y = 16;
  const LH = 14;
  if (logo) {
    const maxH = 12, maxW = 54;
    let lh = maxH, lw = (logo.w / logo.h) * lh;
    if (lw > maxW) { lw = maxW; lh = (logo.h / logo.w) * lw; }
    if (logoOnTile) {
      doc.setFillColor(...PRIMARY);
      doc.roundedRect(M, y, lw + 8, LH, RADIUS, RADIUS, 'F');
      doc.addImage(logo.data, 'PNG', M + 4, y + (LH - lh) / 2, lw, lh);
    } else {
      doc.addImage(logo.data, 'PNG', M, y + (LH - lh) / 2, lw, lh);
    }
  } else {
    type('semibold', 18, FG);
    doc.text(businessName, M, y + 9);
  }
  const sender = [
    ...lines(business.address).slice(0, 4),
    [business.phone, business.email].filter(Boolean).join('  ·  '),
    [business.vatNumber && `VAT no. ${business.vatNumber}`, business.registrationNumber && `Reg. no. ${business.registrationNumber}`].filter(Boolean).join('  ·  '),
  ].filter(Boolean);
  type('semibold', 9.5, FG);
  doc.text(businessName, R, y + 3.5, { align: 'right' });
  type('regular', 8.5, MUTED_FG);
  sender.forEach((line, i) => doc.text(line, R, y + 8 + i * 4, { align: 'right' }));
  y += Math.max(LH, 8 + sender.length * 4) + 5;
  rule(M, y, R);

  // ── Title ──
  y += 12;
  type('semibold', 22, FG);
  doc.text(taxInvoice ? 'Tax invoice' : 'Invoice', M, y);
  type('regular', 9.5, MUTED_FG);
  doc.text(invoice.number || 'Draft', R, y, { align: 'right' });

  // ── Summary: amount, status pill, dates ──
  y += 8;
  const sumH = 40;
  card(M, y, CW, sumH);
  type('medium', 8.5, MUTED_FG);
  doc.text(paid > 0 && balance > 0 ? 'Balance due' : (status === 'paid' ? 'Total paid' : 'Amount due'), M + 6, y + 8.5);
  type('semibold', 24, FG);
  doc.text(money(status === 'paid' ? t.total : (paid > 0 ? balance : t.total)), M + 6, y + 19);
  type('medium', 8, pill.fg);
  const pw = doc.getTextWidth(pill.label) + 10;
  doc.setFillColor(...pill.bg);
  doc.roundedRect(R - 6 - pw, y + 6.5, pw, 6.4, 3.2, 3.2, 'F');
  doc.setFillColor(...pill.fg);
  doc.circle(R - 6 - pw + 3.6, y + 9.7, 0.8, 'F');
  doc.text(pill.label, R - 6 - pw + 6, y + 11.1);
  rule(M, y + 24, R);
  const meta = [['Invoice date', longDate(invoice.issueDate)], ['Due date', longDate(invoice.dueDate)], ['Your reference', invoice.client?.reference || '—']];
  const colW = CW / 3;
  meta.forEach(([k, v], i) => {
    const x = M + 6 + i * colW;
    type('medium', 8, MUTED_FG); doc.text(k, x, y + 30);
    type('medium', 10, FG); doc.text(wrap(v, colW - 8)[0], x, y + 35.5);
  });

  // ── Billed to | Payment details ──
  y += sumH + 7;
  const c = invoice.client || {};
  const billed = [
    ...(c.company ? [[c.company, true]] : []),
    ...(c.name ? [[c.name, !c.company]] : []),
    ...lines(c.address).flatMap(l => wrap(l, CW / 2 - 12).map(x => [x, false])),
    ...(c.email ? [[c.email, false]] : []),
    ...(c.vatNumber ? [[`VAT no. ${c.vatNumber}`, false]] : []),
  ];
  const payTo = [
    business.bankName && ['Bank', business.bankName],
    business.accountName && ['Account name', business.accountName],
    business.accountNumber && ['Account no.', business.accountNumber],
    business.branchCode && ['Branch code', business.branchCode],
    business.accountType && ['Account type', business.accountType],
    invoice.number && ['Reference', invoice.number],
  ].filter(Boolean);
  const instr = business.paymentInstructions ? wrap(business.paymentInstructions, CW / 2 - 12) : [];
  const leftH = billed.length * 4.8, rightH = payTo.length * 4.7 + instr.length * 4.4 + (instr.length ? 2 : 0);
  const cardH = 17 + Math.max(leftH, rightH, 10);
  card(M, y, CW, cardH);
  doc.setDrawColor(...BORDER); doc.line(M + CW / 2, y + 5, M + CW / 2, y + cardH - 5);
  type('medium', 8.5, MUTED_FG); doc.text('Billed to', M + 6, y + 8.5); doc.text('How to pay', M + CW / 2 + 6, y + 8.5);
  billed.forEach(([line, strong], i) => { type(strong ? 'semibold' : 'regular', 10, strong ? FG : [63, 63, 70]); doc.text(String(line), M + 6, y + 15 + i * 4.8); });
  payTo.forEach(([k, v], i) => { type('regular', 9, MUTED_FG); doc.text(k, M + CW / 2 + 6, y + 15 + i * 4.7); type('medium', 9.5, FG); doc.text(wrap(v, 52)[0], M + CW / 2 + 34, y + 15 + i * 4.7); });
  if (!payTo.length && !instr.length) { type('regular', 9.5, MUTED_FG); doc.text('No payment details set up.', M + CW / 2 + 6, y + 15); }
  type('regular', 8.5, MUTED_FG);
  instr.forEach((l, i) => doc.text(l, M + CW / 2 + 6, y + 15 + payTo.length * 4.7 + 3 + i * 4.4));

  // ── Items ──
  y += cardH + 7;
  const tableTop = y;
  const pagesBefore = doc.getNumberOfPages();
  const rows = (invoice.items || []).filter(i => i.description || i.quantity || i.unitPrice);
  doc.autoTable({
    startY: y, margin: { left: M, right: M }, tableWidth: CW,
    head: [['Description', 'Qty', 'Unit price', 'Amount']],
    body: rows.map((i, n) => [String(i.description || ''), String(Number(i.quantity) % 1 ? Number(i.quantity).toFixed(2).replace(/0+$/, '') : Number(i.quantity)), money(i.unitPrice), money(t.lines[(invoice.items || []).indexOf(i)] ?? 0)]),
    theme: 'plain',
    styles: { font: FAMILY, fontStyle: WEIGHT.regular, fontSize: 9.5, textColor: FG, cellPadding: { top: 4.2, bottom: 4.2, left: 6, right: 6 }, valign: 'top' },
    headStyles: { fontStyle: WEIGHT.medium, fontSize: 8.5, textColor: MUTED_FG, cellPadding: { top: 4, bottom: 4, left: 6, right: 6 } },
    columnStyles: { 0: { cellWidth: 'auto', fontStyle: WEIGHT.medium }, 1: { halign: 'center', cellWidth: 20 }, 2: { halign: 'right', cellWidth: 34 }, 3: { halign: 'right', cellWidth: 34, fontStyle: WEIGHT.medium } },
    didParseCell: (d) => { if (d.section === 'head' && d.column.index > 0) d.cell.styles.halign = d.column.index === 1 ? 'center' : 'right'; },
    didDrawCell: (d) => {
      const last = d.section === 'body' && d.row.index === rows.length - 1;
      if (!last) { doc.setDrawColor(...BORDER); doc.setLineWidth(0.2); doc.line(d.cell.x, d.cell.y + d.cell.height, d.cell.x + d.cell.width, d.cell.y + d.cell.height); }
    },
  });
  const tableEnd = doc.lastAutoTable?.finalY || y;
  if (doc.getNumberOfPages() === pagesBefore) { doc.setDrawColor(...BORDER); doc.setLineWidth(0.25); doc.roundedRect(M, tableTop, CW, tableEnd - tableTop, RADIUS, RADIUS, 'S'); }

  // ── Totals (right) and notes (left) ──
  let ty = tableEnd + 8;
  const totRows = [
    ['Subtotal', money(t.subtotal)],
    t.discount > 0 && [invoice.discountType === 'percent' ? `Discount (${Number(invoice.discountValue)}%)` : 'Discount', `-${money(t.discount)}`],
    Number(invoice.taxRate) > 0 && [`${invoice.taxLabel || 'VAT'} (${Number(invoice.taxRate)}%)`, money(t.tax)],
  ].filter(Boolean);
  const hasPaid = paid > 0;
  const totW = 84, totH = 12 + totRows.length * 7 + 12 + (hasPaid ? 14 : 0);
  if (ty + totH > H - 24) { doc.addPage(); ty = 20; }
  const totX = R - totW;
  card(totX, ty, totW, totH);
  let ry = ty + 10;
  const trow = (k, v, strong = false) => {
    type(strong ? 'semibold' : 'regular', strong ? 11 : 9.5, strong ? FG : MUTED_FG); doc.text(k, totX + 6, ry);
    type(strong ? 'semibold' : 'medium', strong ? 11 : 9.5, FG); doc.text(v, totX + totW - 6, ry, { align: 'right' });
  };
  totRows.forEach(([k, v]) => { trow(k, v); ry += 7; });
  rule(totX, ry - 3, totX + totW); ry += 5;
  trow('Total', money(t.total), true);
  if (hasPaid) { ry += 7.5; trow('Paid', `-${money(paid)}`); ry += 7; trow('Balance due', money(balance), true); }

  const notesW = CW - totW - 8;
  const noteLines = invoice.notes ? wrap(invoice.notes, notesW - 12) : [];
  if (noteLines.length) {
    const nh = Math.max(totH, 14 + noteLines.length * 4.4);
    card(M, ty, notesW, nh);
    type('medium', 8.5, MUTED_FG); doc.text('Notes', M + 6, ty + 9);
    type('regular', 9, [63, 63, 70]); doc.text(noteLines, M + 6, ty + 15);
  }

  // ── Footer, and a DRAFT mark across a draft ──
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    rule(M, H - 20, R);
    type('regular', 8.5, MUTED_FG);
    doc.text([businessName, business.email].filter(Boolean).join('  ·  '), M, H - 13.5);
    doc.text(`${invoice.number ? `Invoice ${invoice.number}  ·  ` : ''}Page ${p} of ${pages}`, R, H - 13.5, { align: 'right' });
    if (status === 'draft' || status === 'void') {
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.07 }));
      doc.setFont(FAMILY, WEIGHT.semibold); doc.setFontSize(92); doc.setTextColor(...MUTED);
      doc.setTextColor(0, 0, 0);
      doc.text(status === 'void' ? 'VOID' : 'DRAFT', W / 2, H / 2 + 20, { align: 'center', angle: 35 });
      doc.restoreGraphicsState();
    }
  }

  const filename = `Invoice-${invoice.number || 'draft'}.pdf`;
  return {
    doc, filename,
    base64: () => doc.output('datauristring').split('base64,')[1],
    blobUrl: () => URL.createObjectURL(doc.output('blob')),
  };
}
